import { Banner, Heading } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { QuestionCircleIcon } from '@mm/icons';
import { IGWelcomerSlideoutBanner } from '@mm/icons/SlideoutBanners';
import { useIceBreakerCRUDActions } from '@mm/state/actions/IceBreakerCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { WidgetTypes } from '@mm/state/model/WidgetModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { selectIGIceBreaker } from '@mm/state/selectors/iceBreakerSelectors';
import { selectInstagramWelcomer } from '@mm/state/selectors/promoterSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import React, { useEffect } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { Label } from 'reactstrap';
import { PrimaryToolTip } from '../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import {
  getMMdndEvents,
  MMdndContext,
} from '../../../../../common/helpers/MMdndContext';
import { MOBILE_THRESHOLD } from '../../../../constants/screenSize';

import { IceBreaker } from '../../../../features/facebookPageWelcomer/IceBreaker/IceBreaker';
import { IceBreakerPreview } from '../../../../features/facebookPageWelcomer/IceBreaker/IceBreakerPreview/IceBreakerPreview';
import { useWelcomerFilter } from '../../../../features/instagramWelcomer/use-welcomer-filter';
import { EmbeddableDialogueEditorWithPreview } from '../../../../features/startBotPromoters/common/StartBotsDialogueEditor/EmbeddableDialogueEditorWithPreview';
import { PromoterHeader } from '../../components/PromoterHeader/PromoterHeader';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { FreeTrialWarning } from '../../elements/FreeTrialWarning';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './IGWelcomerSlideoutEditor.module.css';

const widgetsToExclude = [
  WidgetTypes.LIST,
  WidgetTypes.ITEM,
  WidgetTypes.VIDEO,
  WidgetTypes.ATTACHMENT,
  WidgetTypes.SMS_OPT_IN,
  WidgetTypes.SMS_OPT_IN_REPLY,
  WidgetTypes.OPT_IN,
  WidgetTypes.OTN,
];

export const IGWelcomerSlideoutEditor = () => {
  const promoterCRUDActions = usePromoterCRUDActions();
  const iceBreakerCRUDActions = useIceBreakerCRUDActions();
  const { setFilterKind, activeKind } = useWelcomerFilter();
  const { setRef } = useSlideoutOnClose();

  const chatbotId = useSelector(activeChatbotIdSelector);
  const promoter = useSelector(selectInstagramWelcomer)?.[0];
  const iceBreaker = useSelector(selectIGIceBreaker);
  const facebookPage = useSelector(activeFacebookPageSelector);
  const instagramUsername =
    facebookPage?.availableInstagramAccountsData?.username;
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;
  const dndEvents = getMMdndEvents();

  useEffect(() => {
    async function fetchItems() {
      await promoterCRUDActions.retrieveAll(chatbotId, 'instagram_welcomer');
      iceBreakerCRUDActions.retrieveAll(chatbotId, 'instagram');
    }
    fetchItems();
  }, [chatbotId, promoterCRUDActions]);

  if (!promoter || !iceBreaker) {
    return null;
  }
  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <PromoterHeader
        promoter={promoter}
        title="Instagram DM Autoresponder"
        hideDeleteAction
      />

      <FreeTrialWarning />

      <SlideoutMenu.Content className={styles.content}>
        <MMdndContext.Provider value={dndEvents}>
          {!isMobile && (
            <Banner
              css={{
                collapsedHeader: styles.collapsedHeader,
                content: styles.bannerContent,
              }}
              withBannerHeader
              headerText="What is an Instagram DM Autoresponder?"
              withLeftArea={false}
              showCloseIcon
              usedInSlideout
            >
              <IGWelcomerSlideoutBanner textColor="white" />
            </Banner>
          )}
          <div className={styles.configArea}>
            <Heading size={4}>
              How do you want to engage with your audience?
            </Heading>
            <div className={styles.input}>
              <input
                type="radio"
                id="use-get-started-checker"
                className="welcomer__radio-input"
                checked={activeKind === 'instagram_welcomer'}
                onChange={() => {
                  setFilterKind('instagram_welcomer');
                  iceBreakerCRUDActions.update(iceBreaker?.botId, {
                    id: iceBreaker.id,
                    active: false,
                  });
                }}
              />
              <Label
                className="welcomer__radio-label"
                for="use-get-started-checker"
              >
                Show Dialogue
              </Label>
              <PrimaryToolTip
                classNameContent="welcomer__type-selector-tooltip-content"
                classNamePopoverContainer="welcomer__tooltip-popover"
                anchorElement={
                  <QuestionCircleIcon
                    className="welcomer__type-selector-question-icon"
                    color="#262626"
                    style={{ width: '14px', height: '14px' }}
                  />
                }
              >
                If you choose this option, the dialogue will be sent after an
                account visitor initiates a conversation.
              </PrimaryToolTip>
            </div>
            <div className={styles.input}>
              <input
                type="radio"
                id="use-icebreakers-checker"
                className="welcomer__radio-input"
                checked={activeKind === 'icebreakers'}
                onChange={() => {
                  setFilterKind('icebreakers');
                  iceBreakerCRUDActions.update(chatbotId, {
                    id: iceBreaker.id,
                    active: true,
                  });
                }}
              />
              <Label
                className="welcomer__radio-label"
                for="use-icebreakers-checker"
              >
                Show Icebreakers
              </Label>
              <div className="welcomer__tooltip-container">
                <PrimaryToolTip
                  classNameContent="welcomer__type-selector-tooltip-content"
                  classNamePopoverContainer="welcomer__tooltip-popover"
                  anchorElement={
                    <QuestionCircleIcon
                      className="welcomer__type-selector-question-icon"
                      color="#262626"
                      style={{ width: '14px', height: '14px' }}
                    />
                  }
                >
                  Icebreakers are pre-populated Frequently Asked Questions that
                  customers can tap on to start a conversation with your
                  instagram account. Use this option if your customers
                  frequently ask the same questions.
                </PrimaryToolTip>
              </div>
            </div>
          </div>
          {activeKind === 'instagram_welcomer' && (
            <EmbeddableDialogueEditorWithPreview
              className={styles.dialogueEditor}
              dialogueEditorClassName={styles.dialogueContainer}
              dialogueId={promoter.pageId}
              forInstagram
              platforms={['instagram']}
              usedInSlideout
              hideDialogueSelector
              withPreview
            />
          )}
          {activeKind === 'icebreakers' && (
            <div className={styles.icebreakersWrapper}>
              <DragDropContext
                onDragStart={dndEvents.onDragStart}
                onDragUpdate={dndEvents.onDragUpdate}
                onDragEnd={dndEvents.onDragEnd}
              >
                <IceBreaker
                  iceBreaker={iceBreaker}
                  platform="instagram"
                  customInfo="Add some frequently asked questions to help customers start a conversation with your Instagram account."
                  className={styles.icebreakerWrapper}
                  widgetsToExclude={widgetsToExclude}
                  usedInSlideout
                />
                {!isMobile && (
                  <IceBreakerPreview
                    iceBreaker={iceBreaker}
                    accountName={instagramUsername}
                    forInstagram
                  />
                )}
              </DragDropContext>
            </div>
          )}
        </MMdndContext.Provider>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
